import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/utils/logger";
import { SecurityAuditService } from "./securityAuditService";
import { getSecureErrorMessage } from "@/utils/securityUtils";

/**
 * Wrap a promise with a timeout to prevent indefinite hanging
 */
const withTimeout = <T>(promise: Promise<T>, timeoutMs: number = 30000): Promise<T> => {
  return Promise.race([
    promise,
    new Promise<never>((_, reject) => 
      setTimeout(() => reject(new Error('Operation timed out. Please try again.')), timeoutMs)
    )
  ]);
};

export const emailUpdateService = {
  /**
   * Request email change with verification
   */
  async requestEmailChange(
    newEmail: string, 
    currentPassword: string
  ): Promise<{ success: boolean; error?: string; validationFailed?: boolean }> {
    const requestId = `email-change-${Date.now()}`;
    logger.debug('Starting email change request', { requestId, newEmail });
    
    try {
      // Get current user
      const { data: { user } } = await withTimeout(supabase.auth.getUser());
      
      if (!user?.email) {
        return { 
          success: false, 
          error: 'No active user session found' 
        };
      }

      // 1. Verify current password first (security check)
      const { error: signInError } = await withTimeout(
        supabase.auth.signInWithPassword({
          email: user.email,
          password: currentPassword,
        })
      );
      
      if (signInError) {
        return { 
          success: false, 
          error: 'Current password is incorrect' 
        };
      }

      // 2. Request email change (Supabase sends verification email automatically)
      const { data: updateData, error: updateError } = await withTimeout(
        supabase.auth.updateUser(
          {
            email: newEmail.trim().toLowerCase(),
          },
          {
            emailRedirectTo: `https://godyspo.com/auth/callback/email-change`
          }
        )
      );

      if (updateError) {
        throw updateError;
      }

      // 3. Validate that new_email was set
      const updatedUser = updateData?.user;
      if (!updatedUser?.new_email) {
        logger.warn('Email change request did not set new_email field', {
          userId: user.id,
          requestedEmail: newEmail,
          requestId
        });
        
        return {
          success: false,
          error: 'Email change request was not processed by the server. Please try again.',
          validationFailed: true
        };
      }

      // 4. Log security event
      await SecurityAuditService.logEmailChange(newEmail);

      logger.debug('Email change request successful', {
        userId: user.id,
        newEmail: updatedUser.new_email,
        requestId,
        duration: Date.now() - parseInt(requestId.split('-')[2])
      });

      return { success: true };
      
    } catch (error: any) {
      logger.error('Email update request failed', { 
        error: error?.message,
        requestId,
        duration: Date.now() - parseInt(requestId.split('-')[2])
      });
      return { 
        success: false, 
        error: getSecureErrorMessage(error) 
      };
    }
  },

  /**
   * Check if email verification is pending
   */
  async checkVerificationStatus(): Promise<{
    pending: boolean;
    newEmail?: string;
  }> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        return { pending: false };
      }

      // Supabase stores new_email in user metadata during verification
      const newEmail = user.new_email;
      
      return {
        pending: !!newEmail,
        newEmail: newEmail,
      };
      
    } catch (error) {
      logger.error('Verification status check failed', error);
      return { pending: false };
    }
  },

  /**
   * Resend verification email
   */
  async resendVerificationEmail(email: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase.auth.resend({
        type: 'email_change',
        email: email,
      });

      if (error) {
        // Check for rate limiting errors
        if (error.message?.includes('rate limit') || error.message?.includes('too many')) {
          return {
            success: false,
            error: 'Too many requests. Please wait a few minutes before trying again.'
          };
        }
        throw error;
      }

      return { success: true };
    } catch (error: any) {
      logger.error('Resend verification email failed', { error: error?.message });
      
      // Provide more specific error messages
      const errorMessage = error.message?.includes('rate') 
        ? 'Rate limit exceeded. Please wait 5-10 minutes before trying again.'
        : 'Failed to resend verification email. Please try again later.';
      
      return {
        success: false,
        error: errorMessage
      };
    }
  },

  /**
   * Cancel pending email change
   */
  async cancelEmailChange(): Promise<{ success: boolean; error?: string }> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user?.new_email) {
        return { 
          success: false, 
          error: 'No pending email change found' 
        };
      }

      // Update user back to current email (cancels the pending change)
      const { error } = await supabase.auth.updateUser({
        email: user.email, // Keep current email
      });

      if (error) {
        throw error;
      }

      return { success: true };
    } catch (error: any) {
      logger.error('Cancel email change failed', { error: error?.message });
      return {
        success: false,
        error: 'Failed to cancel email change. Please try again.'
      };
    }
  }
};
